/**
 * DeclarationManifestation — ce que l'organisateur a annoncé (item 9.1).
 *
 * ─── À QUOI ÇA SERT ─────────────────────────────────────────────────────────
 *
 * Jusqu'ici, la mairie apprenait au téléphone qu'il y aurait une buvette, ou
 * l'apprenait le samedi matin. Le bouton « Générer un arrêté » existait sur la
 * fiche depuis la tranche E, mais rien ne signalait qu'un arrêté était dû.
 *
 * Ce bloc affiche la déclaration, et rien de plus : il ne fabrique aucun acte.
 * L'événement propose, l'agent dispose — un arrêté engage la commune.
 *
 * ─── LE CONTINGENT DES CINQ ─────────────────────────────────────────────────
 *
 * Article L3334-2 du code de la santé publique : cinq autorisations de débit de
 * boissons temporaire par an et par association. Le modèle EVT-002 attend le
 * rang exact. Personne ne comptait ; l'application compte.
 *
 * Elle AVERTIT, elle ne refuse pas (décision Henrico, 31/07). Les associations
 * sportives agréées relèvent d'un régime plus large, et la buvette d'une fête
 * peut être portée par une association autre que celle qui a réservé. Refuser
 * à la place du maire, sur une donnée partiellement connue, déplacerait la
 * décision hors de celui qui en répond.
 */
import { useEffect, useState } from 'react';
import { Beer, TrafficCone, TriangleAlert } from 'lucide-react';
import { api, type ContingentBuvetteDTO } from '@/lib/api';
import type { MaterielReservation } from '@roronoa-collectivites/shared-types';

interface Props {
  reservation: MaterielReservation;
  /** Ouvre la rédaction d'un acte rattaché à cette réservation. */
  onGenererArrete: (reservationId: string) => void;
}

export function DeclarationManifestation({ reservation: r, onGenererArrete }: Props) {
  const [contingent, setContingent] = useState<ContingentBuvetteDTO | null>(null);

  const aDeclare = r.debitBoissons === true
    || (r.besoinVoiePublique ?? '').trim() !== ''
    || (r.manifestationNom ?? '').trim() !== '';

  useEffect(() => {
    // Le compteur ne sert que si une buvette est annoncée : inutile d'interroger
    // le serveur sur toutes les réservations d'un tableau.
    if (r.debitBoissons !== true) return;
    let vivant = true;
    api.materiel.reservations.contingentBuvette(r.id)
      .then((c) => { if (vivant) setContingent(c); })
      .catch(() => { /* le compteur est un confort : son absence ne masque rien */ });
    return () => { vivant = false; };
  }, [r.id, r.debitBoissons]);

  if (!aDeclare) return null;

  return (
    <div className="w-full rounded-r-lg border border-border/60 p-3">
      <p className="text-xs font-medium text-ink">
        Déclaré par l’organisateur
        {r.manifestationNom ? ` — ${r.manifestationNom}` : ''}
      </p>

      {r.debitBoissons && (
        <div className="mt-2">
          <p className="flex items-center gap-1.5 text-sm text-ink">
            <Beer className="h-4 w-4" aria-hidden="true" />
            Buvette annoncée
            {r.manifestationHeureDebut && r.manifestationHeureFin
              ? ` — de ${r.manifestationHeureDebut} à ${r.manifestationHeureFin}`
              : ''}
          </p>
          {r.manifestationLocalisation && (
            <p className="mt-0.5 text-xs text-ink-muted">
              Emplacement : {r.manifestationLocalisation}
            </p>
          )}

          {contingent?.associationId && (
            <p
              className={`mt-1 text-xs ${
                contingent.depasse ? 'font-medium text-urgent-fg' : 'text-ink-muted'
              }`}
            >
              {contingent.depasse && (
                <TriangleAlert className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
              )}
              {contingent.prochainRang}ᵉ autorisation sur {contingent.contingent} en{' '}
              {contingent.annee}
              {contingent.depasse
                ? ' — contingent annuel dépassé (CSP art. L3334-2). La décision reste au maire.'
                : '.'}
            </p>
          )}
          {contingent && !contingent.associationId && (
            <p className="mt-1 text-xs text-ink-muted">
              Réservation sans association bénéficiaire : le contingent annuel ne
              peut pas être suivi automatiquement.
            </p>
          )}

          <button
            type="button"
            onClick={() => onGenererArrete(r.id)}
            className="mt-1.5 text-xs text-tech-fg hover:underline"
          >
            Rédiger l’autorisation de débit de boissons
          </button>
        </div>
      )}

      {(r.besoinVoiePublique ?? '').trim() !== '' && (
        <div className="mt-3">
          <p className="flex items-start gap-1.5 text-sm text-ink">
            <TrafficCone className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              Besoin sur la voie publique
              <span className="block text-xs text-ink-muted">{r.besoinVoiePublique}</span>
            </span>
          </p>
          {/* Aucun modèle n'est présélectionné : fermeture de rue, occupation du
              domaine public et permission de voirie n'ont ni la même base
              juridique ni la même autorité compétente. C'est un acte de police,
              le choix reste à la mairie. */}
          <button
            type="button"
            onClick={() => onGenererArrete(r.id)}
            className="mt-1.5 text-xs text-tech-fg hover:underline"
          >
            Rédiger l’arrêté correspondant
          </button>
        </div>
      )}
    </div>
  );
}
